import axios from 'axios'
import { UserListModel } from 'src/models/user.models'
import { getResponseData } from 'src/queries/network.utils'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'
import {
  AddSecondaryEmailRequest,
  AddSecondaryEmailResponse,
  DeleteSecondaryEmailRequest,
  DeleteSecondaryEmailResponse,
  GetSecondaryEmailsResponse,
  ResendSecondaryEmailVerificationRequest,
  VerifySecondaryEmailRequest,
} from 'src/queries/secondaryEmails/secondaryEmails.models'

/**
 * Pure network layer for the user secondary-email endpoints. No React, no React
 * Query, no Redux store imports — request config is pulled through the one
 * allowed `getRequestConfig` boundary (`RequestConfig.Default` is the exact
 * config the legacy thunks used via `selectAuthedJsonApiRequestConfig`). Errors
 * are normalized to the backend error contract by `getResponseData`.
 */

export const getSecondaryEmails =
  async (): Promise<GetSecondaryEmailsResponse> =>
    getResponseData(
      axios.get<GetSecondaryEmailsResponse>(
        '/useremails',
        getRequestConfig(RequestConfig.Default),
      ),
    )

export const addSecondaryEmail = async (
  request: AddSecondaryEmailRequest,
): Promise<AddSecondaryEmailResponse> =>
  getResponseData(
    axios.post<AddSecondaryEmailResponse>(
      '/useremails',
      request,
      getRequestConfig(RequestConfig.Default),
    ),
  )

export const verifySecondaryEmail = async (
  request: VerifySecondaryEmailRequest,
): Promise<UserListModel> =>
  getResponseData(
    axios.post<UserListModel>(
      '/useremails/verify',
      request,
      getRequestConfig(RequestConfig.Default),
    ),
  )

export const resendSecondaryEmailVerification = async (
  request: ResendSecondaryEmailVerificationRequest,
): Promise<UserListModel> =>
  getResponseData(
    axios.post<UserListModel>(
      '/useremails/resendverification',
      request,
      getRequestConfig(RequestConfig.Default),
    ),
  )

/**
 * DELETE /useremails/ carries the target email in the request body, matching
 * the legacy thunk exactly (axios sends a `data` payload on DELETE). The config
 * is spread after `data` so its `headers` are preserved.
 */
export const deleteSecondaryEmail = async (
  request: DeleteSecondaryEmailRequest,
): Promise<DeleteSecondaryEmailResponse> =>
  getResponseData(
    axios.delete<DeleteSecondaryEmailResponse>('/useremails/', {
      data: request,
      ...getRequestConfig(RequestConfig.Default),
    }),
  )
